import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Clock, AlertTriangle, CreditCard, X } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

export const TrialBadge = () => {
  const { locale, t } = useI18n();
  const [sub, setSub] = useState(null);

  useEffect(() => {
    axios.get(`${API}/subscription/me`).then(res => setSub(res.data)).catch(() => {});
  }, []);

  if (!sub || !sub.trial || sub.status === "active" || sub.status === "owner") return null;

  const days = sub.days_remaining;
  const isExpired = sub.status === "trial_expired";
  const isUrgent = days !== null && days <= 3;

  if (isExpired) {
    return (
      <Link to="/pricing" className="block" data-testid="trial-badge">
        <div className="mx-2 mb-2 px-3 py-2 bg-destructive/10 border border-destructive/30 rounded-lg text-xs text-destructive flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{t("trialBanner.trialExpired")}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link to="/pricing" className="block" data-testid="trial-badge">
      <div className={`mx-2 mb-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 border ${
        isUrgent 
          ? "bg-orange-50 dark:bg-orange-950 border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-300" 
          : "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
      }`}>
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          {locale === "fr" 
            ? `Essai gratuit — ${days}j restants` 
            : `Free trial — ${days}d left`}
        </span>
      </div>
    </Link>
  );
};

export const TrialExpirationBanner = () => {
  const { locale, t } = useI18n();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/subscription/me`).then(res => setSub(res.data)).catch(() => {});
  }, []);

  if (dismissed || !sub) return null;

  // Owner and active subscribers don't see the banner
  if (sub.status === "owner" || sub.status === "active") return null;

  const isExpired = sub.status === "trial_expired";
  const isUrgent = sub.trial && sub.days_remaining !== null && sub.days_remaining <= 3 && !isExpired;

  if (!isExpired && !isUrgent) return null;

  return (
    <div
      className={`rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${
        isExpired 
          ? "bg-destructive/10 border border-destructive/30" 
          : "bg-orange-50 dark:bg-orange-950 border border-orange-300 dark:border-orange-800"
      }`}
      data-testid="trial-expiration-banner"
    >
      {isExpired ? (
        <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0" />
      ) : (
        <Clock className="w-6 h-6 text-orange-600 flex-shrink-0" />
      )}
      <div className="flex-1">
        <p className="font-medium text-sm">
          {isExpired
            ? t("trialBanner.trialExpiredMessage")
            : (locale === "fr" 
                ? `Plus que ${sub.days_remaining} jour${sub.days_remaining > 1 ? 's' : ''} d'essai gratuit !`
                : `Only ${sub.days_remaining} day${sub.days_remaining > 1 ? 's' : ''} left in your free trial!`)}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {isExpired
            ? t("trialBanner.subscribePrompt")
            : t("trialBanner.dontLoseAccess")}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/pricing">
          <Button size="sm" className="gap-1" data-testid="trial-upgrade-btn">
            <CreditCard className="w-3.5 h-3.5" />
            {locale === "fr" ? "S'abonner — 29€/mois" : "Subscribe — €29/mo"}
          </Button>
        </Link>
        {!isExpired && (
          <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
